import { createSlice } from '@reduxjs/toolkit';

import type { FluxoArbitralListItem, LexiAtiva } from '../types';

import {
  arquivarFluxoArbitral,
  criarFluxoArbitral,
  duplicarFluxoArbitral,
  fetchFluxosArbitrais,
  fetchLexisAtivas,
  publicarFluxoArbitral,
} from './actions';

export interface FluxosArbitraisState {
  items: FluxoArbitralListItem[];
  total: number;
  page: number;
  limit: number;
  loading: boolean;
  creating: boolean;
  actionLoading: Record<string, { duplicando: boolean; publicando: boolean; arquivando: boolean }>;
  error: string | null;
  lastError: string | null;
  lexisAtivas: LexiAtiva[];
  lexisAtivasStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  lexisAtivasRequestId: string | null;
}

const INITIAL_STATE: FluxosArbitraisState = {
  items: [],
  total: 0,
  page: 1,
  limit: 20,
  loading: false,
  creating: false,
  actionLoading: {},
  error: null,
  lastError: null,
  lexisAtivas: [],
  lexisAtivasStatus: 'idle',
  lexisAtivasRequestId: null,
};

function ensureAction(state: FluxosArbitraisState, id: string) {
  if (!state.actionLoading[id]) {
    state.actionLoading[id] = { duplicando: false, publicando: false, arquivando: false };
  }
}

const slice = createSlice({
  name: 'fluxosArbitrais',
  initialState: INITIAL_STATE,
  reducers: {
    clearFluxosArbitraisError(state) {
      state.lastError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLexisAtivas.pending, (state, action) => {
        state.lexisAtivasStatus = 'loading';
        state.lexisAtivasRequestId = action.meta.requestId;
      })
      .addCase(fetchLexisAtivas.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.lexisAtivasRequestId) return;
        state.lexisAtivas = action.payload;
        state.lexisAtivasStatus = 'succeeded';
      })
      .addCase(fetchLexisAtivas.rejected, (state, action) => {
        if (action.meta.requestId !== state.lexisAtivasRequestId) return;
        state.lexisAtivasStatus = 'failed';
      })
      .addCase(fetchFluxosArbitrais.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFluxosArbitrais.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
      })
      .addCase(fetchFluxosArbitrais.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? action.error.message ?? 'Erro';
      })
      .addCase(criarFluxoArbitral.pending, (state) => {
        state.creating = true;
        state.lastError = null;
      })
      .addCase(criarFluxoArbitral.fulfilled, (state, action) => {
        state.creating = false;
        state.items = [action.payload, ...state.items];
        state.total += 1;
      })
      .addCase(criarFluxoArbitral.rejected, (state, action) => {
        state.creating = false;
        state.lastError = action.payload ?? action.error.message ?? 'Erro';
      })
      .addCase(duplicarFluxoArbitral.pending, (state, action) => {
        const id = action.meta.arg;
        ensureAction(state, id);
        state.actionLoading[id].duplicando = true;
      })
      .addCase(duplicarFluxoArbitral.fulfilled, (state, action) => {
        const id = action.meta.arg;
        ensureAction(state, id);
        state.actionLoading[id].duplicando = false;
        state.items = [action.payload, ...state.items];
        state.total += 1;
      })
      .addCase(duplicarFluxoArbitral.rejected, (state, action) => {
        const id = action.meta.arg;
        ensureAction(state, id);
        state.actionLoading[id].duplicando = false;
        state.lastError = action.payload ?? action.error.message ?? 'Erro';
      })
      .addCase(publicarFluxoArbitral.pending, (state, action) => {
        const id = action.meta.arg;
        ensureAction(state, id);
        state.actionLoading[id].publicando = true;
      })
      .addCase(publicarFluxoArbitral.fulfilled, (state, action) => {
        const id = action.meta.arg;
        ensureAction(state, id);
        state.actionLoading[id].publicando = false;
        state.items = state.items.map((f) => (f.id === action.payload.id ? action.payload : f));
      })
      .addCase(publicarFluxoArbitral.rejected, (state, action) => {
        const id = action.meta.arg;
        ensureAction(state, id);
        state.actionLoading[id].publicando = false;
        state.lastError = action.payload?.mensagem ?? action.error.message ?? 'Erro';
      })
      .addCase(arquivarFluxoArbitral.pending, (state, action) => {
        const id = action.meta.arg;
        ensureAction(state, id);
        state.actionLoading[id].arquivando = true;
      })
      .addCase(arquivarFluxoArbitral.fulfilled, (state, action) => {
        const id = action.meta.arg;
        ensureAction(state, id);
        state.actionLoading[id].arquivando = false;
        state.items = state.items.map((f) => (f.id === action.payload.id ? action.payload : f));
      })
      .addCase(arquivarFluxoArbitral.rejected, (state, action) => {
        const id = action.meta.arg;
        ensureAction(state, id);
        state.actionLoading[id].arquivando = false;
        state.lastError = action.payload ?? action.error.message ?? 'Erro';
      });
  },
});

export const { clearFluxosArbitraisError } = slice.actions;
export const fluxosArbitraisReducer = slice.reducer;
